import {
  faCheck,
  faTag,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover } from '@mui/material';

import { Typography } from '@design-system/components/typography/typography';

import {
  ACTION_LABEL,
  ALL_READINESS_FIELDS,
  isReady,
  metCount,
  totalCount,
} from '../../model/programs.readiness';
import { ProgramReadiness } from '../../model/programs.types';

import { ReadinessIndicator } from './ReadinessIndicator';
import * as styles from './ProgramReadinessPopover.css';

const HEADER_ID = 'program-readiness-popover-header';

type ProgramReadinessPopoverProps = {
  anchor: HTMLElement | null;
  readiness: ProgramReadiness | null;
  onClose: () => void;
};

/**
 * Anchored popover that surfaces publication readiness as a checklist of
 * action items. Three layers:
 *  1. Header pill — `N of 4 required` with progress ring or solid check.
 *  2. Banner — orange "not ready" warning or green "ready to sell" message.
 *  3. Criterion list — each row is a rounded pill; met rows are faded with
 *     a strike-through label, unmet rows are bold black with an open ring.
 */
export const ProgramReadinessPopover = ({
  anchor,
  readiness,
  onClose,
}: ProgramReadinessPopoverProps) => {
  const open = anchor != null && readiness != null;

  return (
    <Popover
      open={open}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          'aria-labelledby': HEADER_ID,
          className: styles.paper,
        },
      }}
    >
      {readiness ? (
        <div className={styles.container} role="group">
          <HeaderPill readiness={readiness} />
          <Banner readiness={readiness} />
          <CriterionList readiness={readiness} />
        </div>
      ) : null}
    </Popover>
  );
};

const HeaderPill = ({ readiness }: { readiness: ProgramReadiness }) => (
  <div className={styles.headerPill} id={HEADER_ID}>
    <ReadinessIndicator readiness={readiness} size={20} />
    <Typography variant="body" size="medium" fontWeight="bold">
      {metCount(readiness)} of {totalCount()} required
    </Typography>
  </div>
);

const Banner = ({ readiness }: { readiness: ProgramReadiness }) => {
  const ready = isReady(readiness);
  return (
    <div className={styles.banner({ tone: ready ? 'success' : 'warning' })}>
      <FontAwesomeIcon
        icon={ready ? faTag : faTriangleExclamation}
        aria-hidden
        className={styles.bannerIcon}
      />
      <Typography variant="body" size="small" fontWeight="medium">
        {ready
          ? 'Program is validated & ready to sell in offer.'
          : 'Program is not ready to be sold'}
      </Typography>
    </div>
  );
};

const CriterionList = ({ readiness }: { readiness: ProgramReadiness }) => (
  <ul className={styles.list}>
    {ALL_READINESS_FIELDS.map((field) => {
      const met = readiness[field];
      return (
        <li key={field} className={styles.item({ met })}>
          <span className={styles.itemIcon({ met })} aria-hidden>
            {met ? <FontAwesomeIcon icon={faCheck} /> : null}
          </span>
          <Typography
            variant="body"
            size="medium"
            fontWeight="normal"
            className={styles.label({ met })}
          >
            {ACTION_LABEL[field]}
          </Typography>
          <span className={styles.srOnly}>{met ? 'met' : 'not met'}</span>
        </li>
      );
    })}
  </ul>
);
